import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Modal, Select, Spin, message } from 'antd';
import { API } from '@/services/api.service';
import { errorText } from './format';

/**
 * Puts a company from the caller's portfolio on the DI1 list, then reads its
 * members from APR so Table 1 is filled straight away.
 */
export default function AddCompanyModal({ open, onClose, onAdded }) {
  const { t } = useTranslation('diReports');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['di-candidates', q],
    queryFn: () => API.diReports.getCandidates(q),
    enabled: open,
  });

  const add = useMutation({
    mutationFn: async (companyId) => {
      await API.diReports.saveProfile(companyId, { obligor: true });
      // APR may be slow or refuse; the company stays on the list either way.
      await API.diReports.refreshFromApr(companyId).catch(() => null);
      return companyId;
    },
    onSuccess: (companyId) => {
      setPicked(null);
      onAdded(companyId);
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('add.title')}
      okText={t('actions.add')}
      onOk={() => picked && add.mutate(picked)}
      okButtonProps={{
        shape: 'round',
        disabled: !picked,
        loading: add.isPending,
      }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      <p className="text-sm opacity-70">{t('add.help')}</p>
      <Select
        showSearch
        filterOption={false}
        onSearch={setQ}
        value={picked}
        onChange={setPicked}
        notFoundContent={isFetching ? <Spin size="small" /> : null}
        options={options.map((c) => ({
          value: c._id,
          label: `${c.name} · ${c.mb || '—'}`,
        }))}
        placeholder={t('add.placeholder')}
        style={{ width: '100%' }}
      />
      {add.isPending && <p className="mt-3 text-sm">{t('owners.aprWait')}</p>}
    </Modal>
  );
}
